import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { suggest_InfoCircleLine_str } from '@zenlayer/zen-design-icons';
import { CHARGE_TYPE, isPostpaidChargeType, isPrepaidChargeType, useHistory, } from '@zenlayer/portal-utils';
import { Day, Descriptions, SvgIcon, Space, Tooltip, } from '@zenlayer/zen-design';
import dayjs, { utc2local } from '@zenlayer/zen-design/esm/_util/day';
import { pickDomAttrs } from '@zenlayer/zen-design/esm/_util/utils';
import { listUrlParamsStringify } from '@zenlayer/zen-design/esm/hooks/useRequest';
import t from '@zenlayer/zen-intl18';
import { useMemo } from 'react';
import { LinkButton } from '../link-button';
import { getNetworkPricingModel, } from './networkPricingModel';
import { PricingModel } from './pricingModel';
import { TextBlock } from './share';
var RECYCLE_STATUS;
(function (RECYCLE_STATUS) {
    RECYCLE_STATUS["RETURNED"] = "RETURNED";
    RECYCLE_STATUS["WAIT"] = "WAIT";
    RECYCLE_STATUS["DESTROYED"] = "DESTROYED";
    RECYCLE_STATUS["PENDING"] = "PENDING";
    RECYCLE_STATUS["CANCELED"] = "CANCELED";
})(RECYCLE_STATUS || (RECYCLE_STATUS = {}));
/**
 *
 * ### Minimal usage example
 * ```jsx
 *  <OrderAndBilling
      data={detailData}
      onActionSuccess={updateBasicInfo}
      checkAuth={checkAuth}
      teamConfig={teamConfig}
    />
 * ```
 */
export const OrderAndBilling = (props) => {
    const { data, onActionSuccess, teamConfig, checkAuth, className, style, dataAttrs, } = props;
    const history = useHistory();
    const { uuid, createTime } = data;
    const instanceChargeType = (data.instanceChargeType ??
        data.diskChargeType ??
        data.chargeType);
    const orders = useMemo(() => {
        if (!uuid || instanceChargeType === CHARGE_TYPE.OFFLINE) {
            return undefined;
        }
        return (_jsx(Descriptions.Item, { label: t('sGN4ooMvjEUK'), children: _jsx("a", { ...pickDomAttrs(dataAttrs?.orderLink).data, onClick: () => {
                    history.push(`/account/order?${listUrlParamsStringify({
                        resourceId: data?.uuid,
                    }, {
                        type: isPostpaidChargeType(instanceChargeType)
                            ? 'PostpaidOrder'
                            : 'PrepaidOrder',
                    })}`);
                }, children: t('hYrf2PoJ0OKE') }) }));
    }, [uuid, instanceChargeType, dataAttrs?.orderLink]);
    const billingTime = useMemo(() => {
        if (data.maintenanceStatus === 'BROKEN' && data.approvalDueTime) {
            return (_jsx(Descriptions.Item, { label: t('QIteAlR2vTrd'), children: _jsx(Day.CountDown, { targetDate: data.approvalDueTime, onEnd: onActionSuccess, format: ({ text }) => {
                        return (_jsxs(TextBlock, { type: "error", children: [_jsx("span", { children: t('e7ALXEW7IBdA') }), _jsxs("span", { children: [_jsx("span", { children: text }), _jsx("span", { children: t('5ppMS502tmBn') })] })] }));
                    } }) }));
        }
        if (data.promiseDueTime && dayjs().isBefore(data.promiseDueTime)) {
            return (_jsx(Descriptions.Item, { label: t('QIteAlR2vTrd'), children: _jsx(Day.CountDown, { targetDate: data.promiseDueTime, onEnd: onActionSuccess, format: ({ text }) => {
                        return (_jsxs(TextBlock, { children: [_jsx("span", { children: t('viTuLVEEkpUS') }), _jsxs("span", { children: [_jsx("span", { children: text }), _jsx("span", { children: t('iUGziYkzlInh') })] })] }));
                    } }) }));
        }
        if (isPrepaidChargeType(instanceChargeType) && data.dueTime) {
            return (_jsx(Descriptions.Item, { label: t('QIteAlR2vTrd'), children: _jsx(Day.CountDown, { targetDate: data.dueTime, onEnd: onActionSuccess, format: ({ text, formattedRes }) => {
                        const suggestDueTime = teamConfig['GLOBAL:suggestDueTime'];
                        const isAutoRenew = data.subscription === 'NO_SUBSCRIPTION'
                            ? data.renewFlag === 'AUTO_RENEW'
                            : data.subscription === 'SUBSCRIPTION';
                        return (_jsx(Tooltip, { title: utc2local(data.dueTime), children: _jsx(TextBlock, { type: !isAutoRenew && formattedRes.days < suggestDueTime
                                    ? 'warning'
                                    : 'default', children: isAutoRenew ? (_jsxs(_Fragment, { children: [_jsx("span", { children: t('G0xyuIbBILZq') }), _jsxs("span", { children: [_jsx("span", { children: text }), _jsx("span", { children: t('H81DYHwLh99m') })] })] })) : (_jsxs(_Fragment, { children: [_jsx("span", { children: t('QY4cI5TF_jWg') }), _jsxs("span", { children: [_jsx("span", { children: text }), _jsx("span", { children: t('cPsXtRrV2oxk') })] })] })) }) }));
                    } }) }));
        }
    }, [data]);
    const networkPricingModel = useMemo(() => {
        let content = getNetworkPricingModel({ data, checkAuth });
        if (content) {
            return (_jsx(Descriptions.Item, { label: t('AJMLuhGjsrht'), children: content }));
        }
    }, [data]);
    const pricingModel = useMemo(() => {
        return (_jsx(Descriptions.Item, { label: networkPricingModel ? t('4CUhUHjkLrd6') : t('kSnSUZrgvlMt'), children: _jsx(PricingModel, { data: data }) }));
    }, [data]);
    const bill = useMemo(() => {
        if (!uuid || instanceChargeType === CHARGE_TYPE.OFFLINE) {
            return undefined;
        }
        return (_jsx(Descriptions.Item, { label: _jsxs(Space, { children: [_jsx("div", { children: t('H8u6LpwFLBL5') }), _jsx(Tooltip, { title: t('si9MZF0sy3TW'), children: _jsx(SvgIcon, { fontSize: 14, block: true, svgStr: suggest_InfoCircleLine_str }) })] }), children: _jsx(LinkButton, { ...dataAttrs?.billLink, href: `/account/bill?${listUrlParamsStringify({
                    instance: uuid,
                    billYear: new Date().getFullYear(),
                }, { tab: 'list' })}`, children: t('iVouTrpNSSEX') }) }));
    }, [data, instanceChargeType, dataAttrs?.billLink]);
    return (_jsxs(Descriptions, { title: t('eSvHVpcG5G7E'), className: className, style: style, children: [orders, _jsx(Descriptions.Item, { label: t('JuAh43dSZTKR'), children: createTime ? _jsx(Day.FromNow, { targetDate: createTime }) : '-' }), billingTime, pricingModel, networkPricingModel, bill] }));
};
export * from './networkPricingModel';
export * from './pricingModel';
